/**
 * Reduces an API error body to the parts that are useful in a log.
 *
 * The backend serialises errors as `{ error, message, ... }`, and those two
 *   strings plus the status are what actually diagnose a failure. Logging the
 *   whole body was how `send_form` failures used to be investigated, and losing
 *   that detail once cost an afternoon — so this keeps the diagnosis and drops
 *   only the parts nobody reads on purpose.
 *
 * Why it matters more than tidiness: `console.error` is a Sentry breadcrumb by
 *   default (`breadcrumbsIntegration` is in `getDefaultIntegrations`, with
 *   `console: true`), and the breadcrumb carries the raw arguments in
 *   `data.arguments`, not just the joined message. Session Replay records those
 *   too. So anything handed to `console.error` leaves the device.
 *
 * That is fine for `{ error, message }`. It is not fine for a body we did not
 *   author: with `show_exceptions` enabled the API answers `5xx` with an HTML
 *   page containing backend source and the caller's `HTTP_AUTHORIZATION`. Today
 *   that body never reaches us — the same responses lose their CORS headers, so
 *   the browser discards them before axios sees a `response` — but the two
 *   defects are being fixed separately, and if the CORS half lands first this
 *   would start shipping backend source into our telemetry.
 *
 * Non-string, non-object bodies (HTML pages, buffers) are reported by shape and
 *   size only, which is enough to recognise "the server sent something unusual"
 *   without carrying it along.
 */
const MAX_MESSAGE_LENGTH = 300;

const truncate = (value: string): string =>
  value.length > MAX_MESSAGE_LENGTH
    ? `${value.slice(0, MAX_MESSAGE_LENGTH)}…[truncated]`
    : value;

export const summariseErrorBody = (
  data: unknown,
): Record<string, unknown> | string => {
  if (data == null) return '<empty body>';

  if (typeof data === 'string') {
    // A string body is either a short plain-text error the API meant for us, or
    //   an HTML error page it did not. Only the former is safe to carry.
    const looksLikeMarkup = /^\s*</.test(data);
    return looksLikeMarkup
      ? `<${data.length} bytes of markup, not logged>`
      : truncate(data);
  }

  if (typeof data !== 'object') return `<${typeof data} body, not logged>`;

  const body = data as Record<string, unknown>;
  const summary: Record<string, unknown> = {};

  // Only fields the contract defines. Anything else is dropped rather than
  //   passed through, so a body that grows a new field cannot leak by default.
  //
  // `reference` earns its place for a specific reason. The API's `500` handler
  //   now answers with a fixed `message` — deliberately, so that an exception
  //   string cannot reach our telemetry through this very allowlist — and puts
  //   a random hex `reference` beside it, keeping the diagnosis in its own log.
  //   That reference is the only thing joining our record of a failure to
  //   theirs. Dropping it would leave us logging "something went wrong" with no
  //   way to ask which something. It is `SecureRandom.hex(6)`, so it carries
  //   nothing about the patient, the case or the caller.
  (
    ['error', 'message', 'form_type', 'upstream_status', 'reference'] as const
  ).forEach(key => {
    const value = body[key];
    if (typeof value === 'string') summary[key] = truncate(value);
    else if (typeof value === 'number') summary[key] = value;
  });

  const droppedKeys = Object.keys(body).filter(key => !(key in summary));
  if (droppedKeys.length) summary.droppedKeys = droppedKeys;

  return summary;
};
